import {
    ButtonCancel,
    ButtonSecondary,
    Colors,
    Heading,
    Icon,
    SelectFilled,
    Text,
    type OptionProps,
} from '@amelio/ui-library';
import { useEffect, useMemo, useState } from 'react';

import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';

import {
    useGetCompetencyProfileDeletionImpact,
    useRemoveCompetencyProfile,
} from '@/api/competency-services/competency-profile';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import { getTextByLanguage } from '@/utils/localization';

type DeleteCompetencyProfileModalProps = {
    profile: CompetencyProfile;
    allProfiles: CompetencyProfile[];
    onCancel: () => void;
};

const NO_REPLACEMENT = '';

export const DeleteCompetencyProfileModal = ({
    profile,
    allProfiles,
    onCancel,
}: DeleteCompetencyProfileModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { handleError } = useError();

    const profileName = getTextByLanguage(profile.name, language);

    const {
        data: impact,
        isFetching: isLoadingImpact,
        isError,
    } = useGetCompetencyProfileDeletionImpact(profile.id);

    const { mutateAsync: removeProfile, isPending: isRemoving } = useRemoveCompetencyProfile();

    const [replacementProfileId, setReplacementProfileId] = useState<string>(NO_REPLACEMENT);

    const replacementOptions = useMemo<OptionProps[]>(() => {
        const candidates = allProfiles
            .filter((p) => p.id !== profile.id && p.isActive)
            .map((p) => ({
                id: p.id,
                value: p.id,
                label: getTextByLanguage(p.name, language),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        return [
            {
                id: NO_REPLACEMENT,
                value: NO_REPLACEMENT,
                label: t('competencyProfiles.deleteProfile.reassignNone'),
            },
            ...candidates,
        ];
    }, [allProfiles, language, profile.id, t]);

    const isSubmitting = isRemoving;

    const employeesCount = impact?.employeesCount ?? 0;
    const showReplacementSelector =
        !isLoadingImpact && !isError && employeesCount > 0 && replacementOptions.length > 1;

    useEffect(() => {
        if (!showReplacementSelector && replacementProfileId !== NO_REPLACEMENT) {
            setReplacementProfileId(NO_REPLACEMENT);
        }
    }, [replacementProfileId, showReplacementSelector]);

    const onConfirm = async () => {
        try {
            await removeProfile({
                id: profile.id,
                replacementProfileId: replacementProfileId || null,
            });
            showSuccessToast({ message: t('competencies.profiles.toast.deleted') });
            onCancel();
        } catch (error) {
            handleError({ error });
        }
    };

    const renderEmphasizedNumbers = (value: string) => {
        return value.split(/(\d+)/).map((chunk, index) =>
            /^\d+$/.test(chunk) ? (
                <span
                    key={index}
                    className={'font-semibold'}
                >
                    {chunk}
                </span>
            ) : (
                <span key={index}>{chunk}</span>
            ),
        );
    };

    return (
        <div className={'flex flex-col gap-4 p-2'}>
            <Heading
                level={4}
                weight={'medium'}
                className={'mb-4 !text-[18px]'}
            >
                <span className={'flex items-center gap-4 text-red-500'}>
                    <Icon
                        name={'essential-danger'}
                        color={'inherit'}
                        className={'h-5 w-5'}
                    />
                    <span>
                        {t('competencyProfiles.deleteProfile.titleWithName', { name: profileName })}
                    </span>
                </span>
            </Heading>

            <div
                className={
                    'border-error/20 bg-error/10 flex flex-col gap-2 rounded-lg border bg-red-50/70 p-5'
                }
            >
                <div className={'flex items-center gap-2'}>
                    <Icon
                        name={'essential-danger'}
                        className={'h-5 w-5'}
                        color={Colors.ERROR}
                    />
                    <Text
                        size={'default'}
                        weight={'medium'}
                        className={'!text-red-500'}
                    >
                        {t('competencyProfiles.deleteProfile.irreversible')}
                    </Text>
                </div>

                {isLoadingImpact ? (
                    <div className={'flex items-center gap-2'}>
                        <div
                            className={
                                'border-base h-4 w-4 animate-spin rounded-full border-2 border-t-transparent'
                            }
                        />
                        <Text
                            size={'default'}
                            color={Colors.BASE_600}
                        >
                            {t('competencyProfiles.deleteProfile.loading')}
                        </Text>
                    </div>
                ) : isError ? (
                    <span className={'text-red-500'}>
                        <Text
                            size={'small'}
                            className={'!text-red-500'}
                        >
                            {t('competencies.cascade.loadError')}
                        </Text>
                    </span>
                ) : impact ? (
                    <Text size={'default'}>
                        <span className={'text-navy'}>
                            {renderEmphasizedNumbers(
                                t('competencyProfiles.deleteProfile.summary', {
                                    groupCount: impact.groupsCount,
                                    competencyCount: impact.competenciesCount,
                                    employeeCount: impact.employeesCount,
                                }),
                            )}
                        </span>
                    </Text>
                ) : null}
            </div>

            {showReplacementSelector && (
                <div className={'mt-1 flex flex-col gap-2'}>
                    <Text
                        size={'default'}
                        weight={'medium'}
                        className={'text-navy'}
                    >
                        {t('competencyProfiles.deleteProfile.reassignLabel')}
                    </Text>

                    <SelectFilled
                        value={replacementProfileId}
                        options={replacementOptions.map((opt) => ({
                            ...opt,
                            label: <span className={'text-navy font-normal'}>{opt.label}</span>,
                        }))}
                        onChange={(value) => setReplacementProfileId(value)}
                        placeholder={t('competencyProfiles.deleteProfile.reassignNone')}
                        sort={'none'}
                        placement={'top-left'}
                        disabled={isSubmitting}
                        dataTestId={'competency-profile-delete-replacement-select'}
                        className={'border border-gray-200 bg-white !font-normal !text-gray-900'}
                    />

                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                    >
                        {t('competencyProfiles.deleteProfile.reassignHelper')}
                    </Text>
                </div>
            )}

            <div className={'mt-2 flex justify-end gap-2'}>
                <ButtonSecondary
                    onClick={onCancel}
                    disabled={isSubmitting}
                    size={'md'}
                    className={'!w-[80px] !min-w-[80px]'}
                >
                    {t('competencyProfiles.deleteProfile.cancel')}
                </ButtonSecondary>

                <ButtonCancel
                    onClick={() => void onConfirm()}
                    loading={isSubmitting}
                    disabled={isLoadingImpact || isSubmitting}
                    size={'md'}
                    dataTestId={'competency-profile-delete-confirm'}
                    variant={'filled'}
                >
                    {t('competencyProfiles.deleteProfile.confirm')}
                </ButtonCancel>
            </div>
        </div>
    );
};
